import { useState, useEffect, useRef } from 'react';
import { isTabletOrMobileDevice } from 'utils/deviceDetect';
import AddImageDesktop from './AddImageDesktop';
import AddImageMobile from './AddImageMobile';
import Button from 'components/buttons/Button';
import { heicTo, isHeic } from 'heic-to';
import Loader from 'components/Loader';
import Overlay from 'components/Overlay';

export default function AddImage({
  setImage,
  vidStream,
  setVidStream,
  setFileName,
}) {
  const [videoSize, setVideoSize] = useState({ height: 0, width: 320 });
  const [isDesktop, setIsDesktop] = useState(false);
  const [isConverting, setIsConverting] = useState(false);

  // ✅ NEW: controls whether the modal is visible on desktop
  const [isCamOpen, setIsCamOpen] = useState(false);

  // ✅ NEW: refs instead of document.getElementById
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  const stopStream = () => {
    if (vidStream) {
      try {
        vidStream.getTracks().forEach((track) => track.stop());
      } catch (e) {
        // no-op
      }
    }
    setVidStream(null);
    setIsCamOpen(false);
  };

  const takePicture = (e) => {
    e.preventDefault();

    const video = videoRef.current;
    const canvas = canvasRef.current;

    if (!video || !canvas) return;

    const context = canvas.getContext('2d');
    context.drawImage(video, 0, 0, videoSize.width, videoSize.height);

    const data = canvas.toDataURL('image/png');
    setImage(data);

    stopStream();
  };

  const openCam = () => {
    // ✅ Open the modal immediately so the <video> exists for attaching stream
    setIsCamOpen(true);

    const All_mediaDevices = navigator.mediaDevices;
    if (!All_mediaDevices || !All_mediaDevices.getUserMedia) {
      console.log('getUserMedia() not supported.');
      return;
    }

    All_mediaDevices.getUserMedia({
      audio: false,
      video: true,
    })
      .then(function (stream) {
        setVidStream(stream);
      })
      .catch(function (e) {
        console.log(e.name + ': ' + e.message);
        setIsCamOpen(false);
      });
  };

  // ✅ When the modal is open AND we have a stream, attach it to the video element
  useEffect(() => {
    const video = videoRef.current;
    if (!isDesktop || !isCamOpen || !vidStream || !video) return;

    if ('srcObject' in video) {
      video.srcObject = vidStream;
    } else {
      video.src = window.URL.createObjectURL(vidStream);
    }

    video.onloadedmetadata = function () {
      setVideoSize({ width: video.videoWidth, height: video.videoHeight });
      video.play();
    };
  }, [isDesktop, isCamOpen, vidStream]);

  // Safety: if component unmounts with camera running
  useEffect(() => {
    return () => {
      if (vidStream) {
        try {
          vidStream.getTracks().forEach((track) => track.stop());
        } catch (e) {
          // no-op
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const convertHeic = async (file) => {
    const heicType = await isHeic(file);
    if (!heicType || !file) return false;

    setIsConverting(true);
    const jpeg = await heicTo({
      blob: file,
      type: 'image/jpeg',
      quality: 0.8,
    });
    setIsConverting(false);

    return new File([jpeg], 'converted.jpg', {
      type: 'image/jpeg',
      lastModified: Date.now(),
    });
  };

  const handleSelectFile = async (e) => {
    let selectedFile =
      e.currentTarget?.type === 'file' ? e.currentTarget.files[0] : e;

    setFileName(selectedFile.name);

    // check for HEIC filetype and convert to jpeg if needed
    const heicConverted = await convertHeic(selectedFile);
    selectedFile = heicConverted ? heicConverted : selectedFile;

    const objectURL = window.URL.createObjectURL(selectedFile);
    setImage(objectURL);
  };

  useEffect(() => {
    setIsDesktop(!isTabletOrMobileDevice());
  }, []);

  return (
    <div>
      {isDesktop ? (
        <AddImageDesktop
          handleSelectFile={handleSelectFile}
          openCam={openCam}
        />
      ) : (
        <AddImageMobile handleSelectFile={handleSelectFile} openCam={openCam} />
      )}

      {/* ✅ DESKTOP ONLY MODAL: only takes space when open */}
      {isDesktop && isCamOpen && (
        <div className="fixed inset-0 z-[9998]">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/60" onClick={stopStream} />

          {/* Modal */}
          <div className="relative z-[9999] flex h-full w-full items-center justify-center p-4">
            <div className="w-full max-w-3xl rounded-2xl bg-white shadow-xl">
              <div className="flex items-center justify-between border-b px-4 py-3">
                <p className="text-sm font-semibold">Camera</p>
                <button
                  type="button"
                  onClick={stopStream}
                  className="rounded-lg px-3 py-1 text-sm hover:bg-stone-100"
                >
                  Close
                </button>
              </div>

              <div className="flex flex-col items-center justify-center gap-4 p-4">
                <video
                  ref={videoRef}
                  id="videoCam"
                  width={videoSize.width}
                  height={videoSize.height}
                  className="max-h-[70vh] w-full rounded-xl bg-black object-contain"
                >
                  Video stream not available.
                </video>

                <div className="flex items-center justify-center gap-3 pb-2">
                  {vidStream && (
                    <Button
                      id="takephoto"
                      onClick={takePicture}
                      classes="font-bold"
                    >
                      Take Photo
                    </Button>
                  )}

                  <Button
                    onClick={stopStream}
                    theme="outline"
                    classes="font-bold"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* canvas stays hidden as before */}
      <canvas
        ref={canvasRef}
        id="canvas"
        className="absolute opacity-0 max-w-full"
        width={videoSize.width}
        height={videoSize.height}
      >
        {' '}
      </canvas>

      {isConverting && (
        <>
          <div
            data-testid="registering-text"
            className="absolute inset-0 z-[9999] flex justify-center items-center"
          >
            <Loader title="Please wait... Converting HEIC format to JPEG" />
          </div>{' '}
          <Overlay show={isConverting} />
        </>
      )}
    </div>
  );
}
